import type { StringBoolean, Customer } from '../types';
import AjaxService from 'services/ajax';

const ajax = AjaxService();

type CreateAddressRequest = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  customerId: string;
  firstName: string;
  isDefault: boolean | StringBoolean;
  lastName: string;
  state: string;
  zip: string;
};

type UpdateAddressRequest = Omit<CreateAddressRequest, 'customerId'> & {
  id: number;
};

type CreateMeasurementRequest = {
  age: number | null;
  algorithmId: number;
  armLength: number;
  buildProfile: string | null;
  chest: number;
  customerId: string;
  cxComments: string;
  fitPreference: string;
  height: number;
  neck: number;
  outseam: number;
  overarm: number;
  pantWaist: number;
  shoeSize: number | null;
  shoeWidth: string | null;
  stomach: number;
  stomachProfile: string | null;
  waist: number;
  width: number;
};

type UpdateMeasurementRequest = Omit<CreateMeasurementRequest, 'cxComments'> & {
  id: string;
  algorithmId: number | null;
};

const accountsApi = {
  updateAddress(data: UpdateAddressRequest) {
    return ajax.post('/api/update-address', {
      body: data,
    });
  },

  createAddress(data: CreateAddressRequest) {
    return ajax.post('/api/create-address', {
      body: data,
    });
  },

  validateCustomerEmail(email: string) {
    return ajax.post('/api/validate-customer-email', {
      body: { email },
    });
  },

  updateCustomerV2(customerId: string, customerData: Customer) {
    return ajax.post('/api/update-customer', {
      body: { customerId, customerData },
    });
  },

  deleteAddress(addressId: number) {
    return ajax.post('/api/delete-address', {
      body: { addressId },
    });
  },

  updateMeasurements(data: UpdateMeasurementRequest) {
    return ajax.post('/api/update-measurements', {
      body: data,
    });
  },

  getMeasurements() {
    return ajax.post('/api/get-measurements');
  },

  createMeasurements(data: CreateMeasurementRequest) {
    return ajax.post('/api/create-measurements', {
      body: data,
    });
  },

  forgotPassword(email: string) {
    return ajax.post('/api/forgot-password', {
      body: { email },
    });
  },

  signUpInvitedMember(customerId: string, password: string) {
    return ajax.post('/api/sign-up-invited-member', {
      body: { customerId, password },
    });
  },

  customerServiceLogin(email: string, token: string) {
    return ajax.post<Customer>('/api/customer-service/login', {
      body: {
        customer_email: email,
        cs_token: token,
      },
    });
  },
};

export const loginV3 = async (email: string, password: string): Promise<Customer> => {
  const data = (await ajax.post('/api/v3/customer-login', {
    body: {
      email,
      password,
    },
  })) as Customer;

  if (!data?.email) {
    throw new Error('Data returned does not have email.');
  }

  return data;
};

export const resetPassword = async (password: string, authToken: string): Promise<Customer> => {
  const data = (await ajax.post(`/api/reset-password`, {
    body: {
      password,
      authToken,
    },
  })) as Customer;

  if (!data?.email) {
    throw new Error("Customer's email is missing from returned data.");
  }

  return data;
};

export default accountsApi;
